import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  children: React.ReactNode;
  content: string | React.ReactNode;
  position?: "top" | "bottom" | "left" | "right";
  delay?: number;
  className?: string;
  theme?: "light" | "dark" | "cute";
  usePortal?: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  position = "top",
  delay = 300,
  className = "",
  theme = "dark",
  usePortal = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isReady, setIsReady] = useState(false); // New state to prevent premature rendering
  const [actualPosition, setActualPosition] = useState(position);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const timeoutRef = useRef<number | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isHoveringRef = useRef(false);

  const calculatePosition = useCallback(() => {
    if (!containerRef.current || !usePortal) return;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    
    // Calculate position relative to viewport
    let top = 0;
    let left = 0;
    let newPosition = position;

    // Determine optimal position based on viewport space
    const spaceTop = rect.top;
    const spaceBottom = window.innerHeight - rect.bottom;
    const spaceLeft = rect.left;
    const spaceRight = window.innerWidth - rect.right;

    if (position === "top" && spaceTop < 60) {
      newPosition = "bottom";
    } else if (position === "bottom" && spaceBottom < 60) {
      newPosition = "top";
    } else if (position === "left" && spaceLeft < 120) {
      newPosition = "right";
    } else if (position === "right" && spaceRight < 120) {
      newPosition = "left";
    }

    // Calculate exact positioning
    switch (newPosition) {
      case "top":
        top = rect.top - 8;
        left = rect.left + rect.width / 2;
        break;
      case "bottom":
        top = rect.bottom + 8;
        left = rect.left + rect.width / 2;
        break;
      case "left":
        top = rect.top + rect.height / 2;
        left = rect.left - 8;
        break;
      case "right":
        top = rect.top + rect.height / 2;
        left = rect.right + 8;
        break;
    }

    setActualPosition(newPosition);
    setTooltipStyle({
      position: "fixed",
      top: `${top}px`,
      left: `${left}px`,
      zIndex: 99999,
      transform: getTransform(newPosition),
      // Add will-change to optimize rendering
      willChange: "transform",
    });
    setIsReady(true);
  }, [position, usePortal]);

  const showTooltip = useCallback(() => {
    isHoveringRef.current = true;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      if (isHoveringRef.current) { // Double-check if still hovering
        // Calculate position first, then show
        calculatePosition();
        setIsVisible(true);
      }
    }, delay);
  }, [delay, calculatePosition]);

  const hideTooltip = useCallback(() => {
    isHoveringRef.current = false;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    setIsVisible(false);
    setIsReady(false);
  }, []);

  // Recalculate position when visible and window resizes
  useEffect(() => {
    if (!isVisible || !usePortal) return;

    const handleResize = () => {
      if (isVisible) {
        calculatePosition();
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, [isVisible, calculatePosition, usePortal]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const getTransform = (pos: string) => {
    switch (pos) {
      case "top":
        return "translate(-50%, -100%)";
      case "bottom":
        return "translate(-50%, 0%)";
      case "left":
        return "translate(-100%, -50%)";
      case "right":
        return "translate(0%, -50%)";
      default:
        return "translate(-50%, -100%)";
    }
  };

  const getTooltipClasses = () => {
    const base = usePortal 
      ? "px-3 py-2 text-sm font-medium rounded-lg shadow-lg pointer-events-none whitespace-nowrap"
      : "absolute px-3 py-2 text-sm font-medium rounded-lg shadow-lg pointer-events-none whitespace-nowrap";

    const themeClasses = {
      dark: "bg-gray-900 text-white border border-gray-700",
      light: "bg-white text-gray-900 border border-gray-200 shadow-xl",
      cute: "bg-gradient-to-r from-pink-400 to-purple-500 text-white border-2 border-white shadow-xl",
    };

    const positionClasses = usePortal ? "" : {
      top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
      bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
      left: "right-full top-1/2 -translate-y-1/2 mr-2",
      right: "left-full top-1/2 -translate-y-1/2 ml-2",
    };

    const positionClass = usePortal ? "" : positionClasses[actualPosition];

    // Smoother transition classes
    const transitionClass = isVisible && isReady 
      ? "opacity-100 transition-opacity duration-150 ease-out" 
      : "opacity-0 transition-opacity duration-75 ease-in";

    return `${base} ${themeClasses[theme]} ${positionClass} ${transitionClass} ${className}`;
  };

  const getArrowClasses = () => {
    const base = "absolute w-0 h-0";
    const arrows = {
      dark: {
        top: "border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900 top-full left-1/2 -translate-x-1/2",
        bottom: "border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-gray-900 bottom-full left-1/2 -translate-x-1/2",
        left: "border-t-4 border-b-4 border-l-4 border-t-transparent border-b-transparent border-l-gray-900 left-full top-1/2 -translate-y-1/2",
        right: "border-t-4 border-b-4 border-r-4 border-t-transparent border-b-transparent border-r-gray-900 right-full top-1/2 -translate-y-1/2",
      },
      light: {
        top: "border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-white top-full left-1/2 -translate-x-1/2",
        bottom: "border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-white bottom-full left-1/2 -translate-x-1/2",
        left: "border-t-4 border-b-4 border-l-4 border-t-transparent border-b-transparent border-l-white left-full top-1/2 -translate-y-1/2",
        right: "border-t-4 border-b-4 border-r-4 border-t-transparent border-b-transparent border-r-white right-full top-1/2 -translate-y-1/2",
      },
      cute: {
        top: "border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-pink-400 top-full left-1/2 -translate-x-1/2",
        bottom: "border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-pink-400 bottom-full left-1/2 -translate-x-1/2",
        left: "border-t-4 border-b-4 border-l-4 border-t-transparent border-b-transparent border-l-pink-400 left-full top-1/2 -translate-y-1/2",
        right: "border-t-4 border-b-4 border-r-4 border-t-transparent border-b-transparent border-r-pink-400 right-full top-1/2 -translate-y-1/2",
      },
    };

    return `${base} ${arrows[theme][actualPosition]}`;
  };

  const tooltipContent = (
    <div
      ref={tooltipRef}
      className={getTooltipClasses()}
      style={usePortal ? tooltipStyle : { zIndex: 99999, position: "absolute" }}
      role="tooltip"
      aria-hidden={!isVisible}
    >
      {content}
      <div className={getArrowClasses()} />
    </div>
  );

  return (
    <div
      ref={containerRef}
      className="relative inline-block"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {children}
      {usePortal && isVisible && isReady
        ? createPortal(tooltipContent, document.body)
        : !usePortal && tooltipContent}
    </div>
  );
};



export default Tooltip;